import { Injectable, signal } from '@angular/core';
import { ClientDocument, ClientDocumentsState, DocumentCategory } from './client-documents.model';

// ---------------------------------------------------------------------------
// Deterministic seeding helpers — same djb2 pattern as other state services
// ---------------------------------------------------------------------------

function djb2(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0;
}

function seedPick<T>(id: string, key: string, options: readonly T[]): T {
  const h = djb2(`${id}:${key}`);
  return options[h % options.length];
}

function isoDate(daysAgo: number, hourOffset = 10): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(hourOffset, 0, 0, 0);
  return d.toISOString();
}

// ---------------------------------------------------------------------------
// Mock document templates
// ---------------------------------------------------------------------------

interface DocumentTemplate {
  fileName: string;
  fileType: string;
  fileSize: number;
  category: DocumentCategory;
  uploadedBy: string;
  daysAgo: number;
}

const DOCUMENT_TEMPLATES: DocumentTemplate[] = [
  {
    fileName: 'Helping_Hands_Application_2024.pdf',
    fileType: 'pdf',
    fileSize: 245678,
    category: 'helping-hands',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 132,
  },
  {
    fileName: 'MPAP_Enrollment_Form.pdf',
    fileType: 'pdf',
    fileSize: 189234,
    category: 'mpap',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 137,
  },
  {
    fileName: 'Equifax_Credit_Report_09_2024.pdf',
    fileType: 'pdf',
    fileSize: 456789,
    category: 'equifax',
    uploadedBy: 'Equifax',
    daysAgo: 151,
  },
  {
    fileName: 'Pay_Stub_October_2024.pdf',
    fileType: 'pdf',
    fileSize: 123456,
    category: 'user-upload',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 146,
  },
  {
    fileName: 'Medical_Bill_Scan.jpg',
    fileType: 'jpg',
    fileSize: 987654,
    category: 'user-upload',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 163,
  },
  {
    fileName: 'W2_Form_2023.pdf',
    fileType: 'pdf',
    fileSize: 234567,
    category: 'other',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 198,
  },
  {
    fileName: 'Bank_Statement_September.xlsx',
    fileType: 'xlsx',
    fileSize: 345678,
    category: 'user-upload',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 148,
  },
  {
    fileName: 'Equifax_Credit_Report_08_2024.pdf',
    fileType: 'pdf',
    fileSize: 456123,
    category: 'equifax',
    uploadedBy: 'Equifax',
    daysAgo: 185,
  },
  {
    fileName: 'Helping_Hands_Supporting_Docs.zip',
    fileType: 'zip',
    fileSize: 1234567,
    category: 'helping-hands',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 135,
  },
  {
    fileName: 'MPAP_Approval_Letter.pdf',
    fileType: 'pdf',
    fileSize: 156789,
    category: 'mpap',
    uploadedBy: 'System',
    daysAgo: 139,
  },
  {
    fileName: 'Utility_Bill_September.pdf',
    fileType: 'pdf',
    fileSize: 234123,
    category: 'user-upload',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 158,
  },
  {
    fileName: 'Rent_Receipt_October.pdf',
    fileType: 'pdf',
    fileSize: 98765,
    category: 'user-upload',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 142,
  },
  {
    fileName: 'Equifax_Credit_Report_07_2024.pdf',
    fileType: 'pdf',
    fileSize: 445678,
    category: 'equifax',
    uploadedBy: 'Equifax',
    daysAgo: 216,
  },
  {
    fileName: 'Insurance_Card_Front.jpg',
    fileType: 'jpg',
    fileSize: 456789,
    category: 'user-upload',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 168,
  },
  {
    fileName: 'Insurance_Card_Back.jpg',
    fileType: 'jpg',
    fileSize: 432123,
    category: 'user-upload',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 168,
  },
  {
    fileName: 'Tax_Document_2023.pdf',
    fileType: 'pdf',
    fileSize: 567890,
    category: 'other',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 193,
  },
  {
    fileName: 'Employment_Verification_Letter.docx',
    fileType: 'docx',
    fileSize: 234567,
    category: 'helping-hands',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 136,
  },
  {
    fileName: 'Budget_Worksheet_2024.xlsx',
    fileType: 'xlsx',
    fileSize: 123456,
    category: 'user-upload',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 173,
  },
  {
    fileName: 'MPAP_Medical_Records.pdf',
    fileType: 'pdf',
    fileSize: 2345678,
    category: 'mpap',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 138,
  },
  {
    fileName: 'ID_Card_Front.png',
    fileType: 'png',
    fileSize: 345678,
    category: 'user-upload',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 178,
  },
  {
    fileName: 'ID_Card_Back.png',
    fileType: 'png',
    fileSize: 321234,
    category: 'user-upload',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 178,
  },
  {
    fileName: 'Equifax_Credit_Report_06_2024.pdf',
    fileType: 'pdf',
    fileSize: 456234,
    category: 'equifax',
    uploadedBy: 'Equifax',
    daysAgo: 247,
  },
  {
    fileName: 'Helping_Hands_Receipt.pdf',
    fileType: 'pdf',
    fileSize: 123456,
    category: 'helping-hands',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 133,
  },
  {
    fileName: 'Expense_Report_September.xlsx',
    fileType: 'xlsx',
    fileSize: 234567,
    category: 'user-upload',
    uploadedBy: 'Pamela Brannan',
    daysAgo: 151,
  },
];

// ---------------------------------------------------------------------------
// Seeding function
// ---------------------------------------------------------------------------

function seedDocuments(sessionId: string): ClientDocumentsState {
  const documents: ClientDocument[] = DOCUMENT_TEMPLATES.map((tmpl, i) => ({
    id: `doc-${sessionId.slice(0, 8)}-${i}`,
    fileName: tmpl.fileName,
    fileType: tmpl.fileType,
    fileSize: tmpl.fileSize,
    category: tmpl.category,
    // Vary the uploader slightly per session using seedPick for the user-upload category
    uploadedBy: tmpl.category === 'user-upload'
      ? seedPick(sessionId, `doc_uploader_${i}`, [tmpl.uploadedBy, tmpl.uploadedBy, 'FA Team'] as const)
      : tmpl.uploadedBy,
    uploadedAt: isoDate(tmpl.daysAgo, 8 + (djb2(`${sessionId}:doc_hour_${i}`) % 10)),
  }));

  // Sort newest first
  documents.sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));

  return { documents };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable()
export class ClientDocumentsStateService {
  private readonly _store = signal<Record<string, ClientDocumentsState>>({});

  readonly storeSnapshot = this._store.asReadonly();

  ensureState(sessionId: string): void {
    if (this._store()[sessionId]) return;
    this._store.update(s => ({ ...s, [sessionId]: seedDocuments(sessionId) }));
  }
}
